import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Box, ButtonBase, IconButton, TextField, Tooltip, Typography } from '@mui/material'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import SequenceMoveContext from './SequenceMoveContext'
import ResequencePreviewPanel from './ResequencePreviewPanel'
import { validatePosition } from './sequencePosition'

// A click on any of these elements does not close the open field: the position triggers and the menu of the rows
// (they replace the open field by their own, in the same click: S205), the arrows (a disabled one does nothing, S118) and the preview panel.
const KEEP_OPEN_SELECTOR = '[data-sequence-keep-open], [data-sequence-preview]'

const arrowStyle = { p: 0, '& .MuiSvgIcon-root': { fontSize: 22 } }

const stopRowClick = (event) => event.stopPropagation()

/**
 * The position field of a row: the current position selected, the allowed range as its help text, Enter to move,
 * Esc (or a click anywhere else) to leave without moving. With the preview switch on, the preview panel hangs from it.
 */
const PositionField = ({ move, row }) => {
  const { t_ } = useSmartTranslation()
  const wrapperRef = useRef()
  const [mounted, setMounted] = useState(false)
  const { field, preview, saving, jobCount } = move
  const closeRef = useRef(move.closeField)
  closeRef.current = move.closeField

  useLayoutEffect(() => setMounted(true), [])

  // Esc, or a click outside the field and its preview, closes it without moving. The click is read on its way
  // down (capture) so that a button of the row that stops its propagation still counts as "outside".
  useEffect(() => {
    const closeOnClickOutside = (event) => {
      if (wrapperRef.current?.contains(event.target) || event.target.closest?.(KEEP_OPEN_SELECTOR)) return
      closeRef.current()
    }
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') closeRef.current()
    }
    document.addEventListener('click', closeOnClickOutside, true)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('click', closeOnClickOutside, true)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [])

  const { valid } = validatePosition(field.text, { current: field.position, max: jobCount })
  const written = field.text !== String(field.position)

  return (
    <Box ref={wrapperRef} sx={{ display: 'inline-block' }}>
      <TextField
        size='small'
        autoFocus
        value={field.text}
        error={written && !valid}
        disabled={saving}
        onChange={(event) => move.changeText(event.target.value)}
        onFocus={(event) => event.target.select()}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            move.confirm()
          }
        }}
        helperText={t_('From 1 to {{max}}', { max: jobCount })}
        inputProps={{ 'aria-label': t_('Position'), inputMode: 'numeric', style: { textAlign: 'center' } }}
        sx={{ width: 110, '& .MuiInputBase-input': { py: 0.5 } }}
      />
      <ResequencePreviewPanel
        anchorEl={move.previewOn && mounted ? wrapperRef.current : undefined}
        preview={preview.status === 'ready' ? preview.data : {}}
        loading={preview.status === 'loading'}
        failed={preview.status === 'error'}
        job={{ fabricationOrder: row.fabricationOrder, job: row.job }}
        onConfirm={() => move.confirm()}
        onCancel={() => move.closeField()}
      />
    </Box>
  )
}

/**
 * What a job shows in the "Position" column of a machine's tab: the `position` Sun calculated for the row
 * (contract §1), as it comes. The screen never numbers the rows itself, nor shows `order` (RN40, R28): a page
 * after a search or the second page of a long queue shows Sun's own numbers. A row with no position leaves the
 * cell empty.
 *
 * With the panel's move controls (a machine's tab, `SequenceMoveContext`), the number is also the way to move the
 * job: pressing it opens the position field (CU3) and, next to it, the up and down arrows (CU6) while the arrows
 * button shows them. None of these clicks reaches the row (RN47).
 *
 * @param {Object} props
 * @param {Object} props.row The list row, with Sun's `position`
 * @param {Object} props.sx The look of the number's box
 */
const SequencePositionCell = ({ row, sx }) => {
  const { t_ } = useSmartTranslation()
  const move = useContext(SequenceMoveContext)
  const hasPosition = Number.isInteger(row.position)
  const number = hasPosition ? row.position : ''

  if (!move.enabled) {
    return <Typography variant='body2' component='span' sx={sx}>{number}</Typography>
  }

  const isOpenHere = move.field.open && move.field.rowId === row.id
  const arrowsLocked = !move.canOrder || move.saving || move.field.open || !hasPosition
  const upDisabled = arrowsLocked || row.position <= 1
  const downDisabled = arrowsLocked || row.position >= move.jobCount
  const canOpen = move.canOrder && !move.saving && hasPosition

  return (
    <Box onClick={stopRowClick} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      {isOpenHere
        ? <PositionField move={move} row={row} />
        : canOpen
          ? (
            <ButtonBase
              data-sequence-keep-open='true'
              onClick={() => move.openField(row)}
              sx={{ ...sx, cursor: 'pointer' }}
            >
              <Typography variant='body2' component='span' sx={{ font: 'inherit' }}>{number}</Typography>
            </ButtonBase>
            )
          : <Typography variant='body2' component='span' sx={sx}>{number}</Typography>}
      {move.arrowsVisible && hasPosition
        ? (
          <Box component='span' sx={{ display: 'inline-flex', flexDirection: 'column' }}>
            <Tooltip title={t_('Move up')}>
              <span data-sequence-keep-open='true'>
                <IconButton size='small' sx={arrowStyle} aria-label={t_('Move up')} data-testid='sequence-arrow-up' disabled={upDisabled} onClick={() => move.moveTo(row, row.position - 1)}>
                  <ArrowDropUpIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t_('Move down')}>
              <span data-sequence-keep-open='true'>
                <IconButton size='small' sx={arrowStyle} aria-label={t_('Move down')} data-testid='sequence-arrow-down' disabled={downDisabled} onClick={() => move.moveTo(row, row.position + 1)}>
                  <ArrowDropDownIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          )
        : null}
    </Box>
  )
}

export default SequencePositionCell
